import { Suspense, useEffect, useRef, useState } from "react"
import { ErrorBoundary } from "../../Common/ErrorBoundry"
import { ToolProps } from "../ActivityFeed/ActivityFeed"
import { MessageApi } from "../../../redux/api/MessageApi"
import { SvgIcon } from '@progress/kendo-react-common'
import * as svg from '@progress/kendo-svg-icons'
import Markdown from "react-markdown"
import ReactTimeago from "react-timeago"
import './Notifications.scss'
import { IMessage } from "../../../types/IMessage"
import Scrollbars from "react-custom-scrollbars-2"
import { EditWindow } from "./MessageEditor"
import { IMessageConfig } from "../../../types/User/IMessageConfig"
import { Api } from "../../../redux/api/BaseApi"
import { IAudience } from "../../../types/User/IAudience"
import { Popover, PopoverActionsBar } from "@progress/kendo-react-tooltip"
import { nanoid } from "nanoid"
import { useCompoundButtonStyles_unstable } from "@fluentui/react-components"

export const Notifications = (props: ToolProps) => {
    const { data } = MessageApi.useGetConfigQuery()
    const [visible, setVisible] = useState<boolean>(false);

    if (!data) return <div className="m-2">Loading...</div>

    let config: IMessageConfig = data;

    const toggleEditor = () => {
        setVisible(!visible);
    };

    if (!config.View_Message_Feed) return (
        <p>Feature disabled</p>
    )

    return (
        <section className='notifications' onMouseLeave={props.onMouseLeave} >
            <ErrorBoundary errorMessage={<div className='m-5'><span className='k-font-icon k-i-close-circle' />Error: Failed to connect to notification service.</div>}>
                <Suspense fallback={'Loading...'}>
                    <header className='right-bar-header'><img alt="notifications icon" className='icon' src='./images/dark-bell-icon.png' /><span className='text' style={{ cursor: "default" }}>Notifications</span>
                        {config.Create_Message && <SvgIcon className="edit-icon" icon={svg.plusCircleIcon} onClick={() => toggleEditor()} />}
                        {!props.fullScreen && <button className='icon lock' onClick={props.unlocked} title='Lock side-bar' ><img alt="lock icon" src='./images/Lock.png' style={{ alignContent: 'center' }} /></button>}
                        {!props.fullScreen && <button className='icon unlock' onClick={props.locked} title='Unlock side-bar' ><img alt="unlock icon" src='./images/Unlock.png' style={{ alignContent: 'center' }} /></button>}
                    </header>
                    {visible && <EditWindow close={toggleEditor} config={config} />}
                    <NotificationsMenu config={config} />
                </Suspense>
            </ErrorBoundary>
        </section >
    )
}

const Audience = ({ messageId }: { messageId: number }) => {
    const [show, setShow] = useState(false);
    const { data: audience, isLoading: audienceLoading } = MessageApi.useGetAudienceQuery(messageId, { skip: !show })
    const iconRef = useRef<HTMLDivElement>(null);
    const { data: profile } = Api.useGetUserProfileQuery()
    const hasClients = audience?.some(v => v.Security_Type_Enum === 3)
    const hasClientTypes = audience?.some(v => v.Security_Type_Enum === 4)
    const hasTeam = audience?.some(v => v.Security_Type_Enum === 1)
    const hasMonitor = audience?.some(v => v.Security_Type_Enum === 2)

    if (profile?.Features.External_Interface) return null

    return (
        <div
            className="audience-icon-wrapper"
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            <div ref={iconRef} className="audience-icon" title="Audience">
                <SvgIcon className="icon-svg" icon={svg.userIcon} />
            </div>

            {audience && <div className="audience-list-wrapper">
                <Popover
                    show={show}
                    anchor={iconRef.current}
                    position="left"
                    className="popover-wrapper"
                    title={'Audience'}
                    animate={false}
                >
                    <ul className="audience-list">
                        {hasTeam && <>
                            <li><u>{profile?.Phrases.Team}s</u></li>
                            {audience?.map(v => {
                                return v.Security_Type_Enum === 1 ? <li key={nanoid()}>{v.Display_Name}</li> : null
                            })}
                        </>}
                        {hasMonitor && <>
                            <li><u>{profile?.Phrases.CallPhrase} Monitor</u></li>
                            {audience?.map(v => {
                                return v.Security_Type_Enum === 2 ? <li key={nanoid()}>{v.Display_Name}</li> : null
                            })}
                        </>}
                        {hasClientTypes && <>
                            <li><u>{profile?.Phrases.Client} Types</u></li>
                            {audience?.map(v => {
                                return v.Security_Type_Enum === 4 && v.Lookup_Id > 0 ? <li key={nanoid()}>{v.Display_Name}</li> : null
                            })}
                        </>}
                        {hasClients && <>
                            <li><u>{profile?.Phrases.Clients}</u></li>
                            {audience?.map(v => {
                                return v.Security_Type_Enum === 3 ? <li key={nanoid()}>{v.Display_Name}</li> : null
                            })}
                        </>}
                    </ul>
                </Popover>
            </div>}
        </div>
    );
};
const NotificationItem = ({ message, config }: { message: IMessage; config: IMessageConfig }) => {
    const { data: user, isLoading } = Api.useGetUserProfileQuery();
    const [closeMessageEndpoint] = MessageApi.useCloseMessageMutation()
    const [deleteMessageEndpoint] = MessageApi.useDeleteMessageMutation()
    const [readMessageEndpoint] = MessageApi.useReadMessageMutation()

    if (isLoading || !user) return null

    const name = `${user.F_Name} ${user.L_Name[0]}`

    let text = message.Text
        .replaceAll(/<(\/|)br[^>]*>/g, "\n")
        .replaceAll("</p>", "\n")
        .replaceAll("<p>", "")
        .replaceAll(/<(\/|)(div|span)[^>]*>/g, "")
        .replaceAll("<strong> ", " **")
        .replaceAll("<strong>", "**")
        .replaceAll("</strong>", "**")

    const matched = [...text.matchAll(/(<a(.*)href=["'])(.*?)(<\/a>)/g)]
    matched.forEach(v => {
        const value = v[0]
        const link = value.match(/(?<=<a(.*)href=["'])(.*?)(?=["'])/)?.[0] ?? ""
        const name = value.match(/(?<=<a(.*)href=["'].*["']>)(.*?)(?=<\/a>)/)?.[0] ?? ""
        text = text.replace(value, `[${name}](${link})`)
    })

    const pfp = message.Profile_Image_Url ?? "./images/placeholder.png"

    return (
        <li className={`notification-item ${message.Is_Read ? "read" : ""}`} onClick={() => readMessageEndpoint(message.Id)}>
            <Audience messageId={message.Id} />
            {message.Important && <SvgIcon className="warning-icon" icon={svg.warningTriangleIcon} />}

            <div className="details">
                <div className="user-details">
                    {config.Show_Profile_Images && <div className="image-container"><img src={pfp} className="profile-picture" alt="profile" /></div>}
                    <p className="user">{message.Created_By_Name}</p>
                    <ReactTimeago live={true} className="time" date={new Date(message.Created + 'Z')} />
                </div>

            </div>
            <div className="text-container">
                <Markdown>{text}</Markdown>
                <div id="buttons">
                    <SvgIcon
                        className="close-btn button-icon"
                        icon={svg.xIcon}
                        onClick={() => closeMessageEndpoint(message.Id)}
                    />
                    {(config.Delete_Any_Message || (config.Delete_Own_Message && name === message.Created_By_Name)) && (
                        <SvgIcon
                            className="delete-btn button-icon"
                            icon={svg.trashIcon}
                            onClick={() => {
                                if (window.confirm("Are you sure you want to permanently delete this message?"))
                                    deleteMessageEndpoint(message.Id)
                            }}
                        />
                    )}
                </div>
            </div>
        </li>
    )
}

const NotificationsMenu = ({ config }: { config: IMessageConfig }) => {
    let { data } = MessageApi.useGetMessagesQuery(undefined, { pollingInterval: 25000 })
    if (!data) return <div className="m-2">Loading...</div>

    // @ts-ignore
    let sortedData = data.toSorted((a: IMessage, b: IMessage) => {
        if (b.Important && !a.Important)
            return 1
        else
            return (new Date(b.Created)).getTime() - (new Date(a.Created)).getTime()
    })

    return (
        <div className="notifications">
            <Scrollbars autoHeight={false} autoHide={true}>
                <ul className="list">
                    {sortedData.map((message: IMessage) => (
                        <NotificationItem key={message.Id} message={message} config={config} />
                    ))}
                </ul>
            </Scrollbars>
        </div>
    )
}